import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    useCaregiverMemory,
    useClientMemory,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const MAX_INSTRUCTION_LENGTH = 500;

/**
 * The memory compile worker bootstraps every memory row with a headers-only
 * template (e.g. "## Care Needs\n\n## Preferences\n\n## Notes") and never
 * overwrites it for entities with nothing to summarize. Headers with no
 * body text are that template, not notes — treat them as empty so thin
 * entities get the empty state instead of a skeleton of blank sections.
 */
function hasSubstantiveContent(markdown: string): boolean {
    return markdown.split('\n').some((line) => {
        const trimmed = line.trim();
        return trimmed !== '' && !trimmed.startsWith('#');
    });
}

function stripNotesHeading(markdown: string): string {
    return markdown.replace(
        /^[ \t]*#+[ \t]+notes[ \t]*(?:#+[ \t]*)?\r?$/gim,
        '',
    );
}

type ProfileNotesPanelProps = {
    readonly organizationId: string;
    readonly entityType: 'client' | 'caregiver';
    readonly entityId: string;
    readonly entityFirstName: string | null;
};

/**
 * Overview "Notes" panel backed by the entity's compiled Phoebe memory.
 * Adding a note posts a memory instruction; the full Memory section keeps
 * the compile/recompile and voice-input flows.
 */
export function ProfileNotesPanel({
    organizationId,
    entityType,
    entityId,
    entityFirstName,
}: ProfileNotesPanelProps) {
    const caregiverMemory = useCaregiverMemory(
        entityType === 'caregiver' ? organizationId : null,
        entityType === 'caregiver' ? entityId : null,
        { reason: 'profile-notes-panel' },
    );
    const clientMemory = useClientMemory(
        entityType === 'client' ? organizationId : null,
        entityType === 'client' ? entityId : null,
        { reason: 'profile-notes-panel' },
    );
    const memoryResult =
        entityType === 'caregiver' ? caregiverMemory : clientMemory;
    const [note, setNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const compiledMarkdown = memoryResult.data?.compiled_markdown?.trim() ?? '';
    const displayMarkdown = stripNotesHeading(compiledMarkdown);
    const hasNotes = hasSubstantiveContent(displayMarkdown);
    const displayName = entityFirstName?.trim() || null;

    const submitNote = async () => {
        const instruction = note.trim();
        if (!instruction || instruction.length > MAX_INSTRUCTION_LENGTH) {
            return;
        }
        setIsSubmitting(true);
        try {
            if (entityType === 'caregiver') {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/caregivers/{caregiver_id}/memory/instructions',
                    pathParams: {
                        organization_id: organizationId,
                        caregiver_id: entityId,
                    },
                    body: { instruction },
                });
            } else {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/clients/{client_id}/memory/instructions',
                    pathParams: {
                        organization_id: organizationId,
                        client_id: entityId,
                    },
                    body: { instruction },
                });
            }
            setNote('');
            toast.success('Phoebe will work this into her notes.', {
                persistNotification: false,
            });
            void memoryResult.mutate?.();
        } catch {
            toast.error('Failed to add the note. Please try again.', {
                persistNotification: false,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-0 flex-col">
            <h3 className="mb-2 text-sm font-semibold">Notes</h3>
            <div className="min-h-0 flex-1 overflow-y-auto text-sm">
                {memoryResult.error ? (
                    <p className="text-destructive" role="alert">
                        Failed to load notes.
                    </p>
                ) : memoryResult.isLoading ? (
                    <Spinner
                        className="size-4"
                        data-testid="profile-notes-loading"
                    />
                ) : hasNotes ? (
                    <Markdown>{displayMarkdown}</Markdown>
                ) : null}
            </div>
            <div className="mt-3 flex gap-2">
                <Input
                    value={note}
                    maxLength={MAX_INSTRUCTION_LENGTH}
                    placeholder={
                        displayName
                            ? `Add a note about ${displayName}…`
                            : 'Add a note…'
                    }
                    onChange={(event) => setNote(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            void submitNote();
                        }
                    }}
                />
                <Button
                    size="sm"
                    disabled={isSubmitting || !note.trim()}
                    onClick={() => void submitNote()}
                >
                    Add
                </Button>
            </div>
        </div>
    );
}
